import { useCallback, useEffect } from 'react';

import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState/onboardingState.ts';
import {
	MEETINGS_PILL_NEW_LOZENGE_FIRST_SEEN_AT_KEY,
	MEETINGS_PILL_NEW_LOZENGE_CLICK_COUNT_KEY,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

// Hide the "New" lozenge once EITHER of these thresholds is reached, whichever comes first.
const MAX_CLICK_COUNT = 3;
const MAX_DAYS_SINCE_FIRST_SEEN = 14;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

type UseMeetingsPillNewLozengeResult = {
	/** Whether the "New" lozenge should be rendered next to the Meetings pill. */
	showLozenge: boolean;
	/** Records a click on the Meetings pill, incrementing the persisted click count. */
	incrementClickCount: () => void;
};

/**
 * Owns the persistence logic for the Meetings pill "New" lozenge.
 *
 * The lozenge is shown until EITHER threshold is reached, whichever comes first:
 *   1. 14 days have elapsed since the user first saw the lozenge, or
 *   2. the user has clicked the Meetings pill 3 times.
 *
 * State is persisted per-user (server-side, cross-device) via two onboarding-state keys:
 *   - `meetings-pill-new-lozenge:firstSeenAt` — ISO timestamp, stamped once on first render
 *   - `meetings-pill-new-lozenge:clickCount`  — running click count
 *
 * @param enabled - only evaluate/persist when the Meetings pill is actually eligible to render.
 */
export const useMeetingsPillNewLozenge = (enabled: boolean): UseMeetingsPillNewLozengeResult => {
	const { data, loading, error } = useGetOnboardingState(
		[MEETINGS_PILL_NEW_LOZENGE_FIRST_SEEN_AT_KEY, MEETINGS_PILL_NEW_LOZENGE_CLICK_COUNT_KEY],
		/* skip */ !enabled,
	);
	const { setOnboardingState } = useSetOnboardingState();

	const state = deserializeState(data);
	const firstSeenAt: string | undefined = state?.firstSeenAt;
	const clickCount = Number(state?.clickCount ?? 0);

	const hasReachedClickLimit = clickCount >= MAX_CLICK_COUNT;
	const hasExpired =
		!!firstSeenAt &&
		Date.now() - new Date(firstSeenAt).getTime() >= MAX_DAYS_SINCE_FIRST_SEEN * MS_PER_DAY;

	const showLozenge = enabled && !loading && !error && !hasReachedClickLimit && !hasExpired;

	// Stamp the first-seen timestamp exactly once, the first time the lozenge is shown.
	// Guard on `!error` so a failed onboarding-state query doesn't record a bogus first-seen time.
	useEffect(() => {
		if (enabled && !loading && !error && !firstSeenAt) {
			setOnboardingState({
				key: MEETINGS_PILL_NEW_LOZENGE_FIRST_SEEN_AT_KEY,
				value: new Date().toISOString(),
			});
		}
	}, [enabled, loading, error, firstSeenAt, setOnboardingState]);

	const incrementClickCount = useCallback(() => {
		// Once the limit is hit there's no need to keep writing.
		if (clickCount >= MAX_CLICK_COUNT) {
			return;
		}
		setOnboardingState({
			key: MEETINGS_PILL_NEW_LOZENGE_CLICK_COUNT_KEY,
			value: String(clickCount + 1),
		});
	}, [clickCount, setOnboardingState]);

	return { showLozenge, incrementClickCount };
};
